import { useEffect, useState } from "react";
import { Play } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function HomePage() {
  const [isLoaded, setIsLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative h-screen flex items-center justify-center overflow-hidden grain-overlay"
      >
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1695289211539-9b8d0adb815e?w=1920&q=80')`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628]/60 via-[#0A1628]/70 to-[#0A1628]" />
        </div>

        {/* Floating Glow */}
        <div className="absolute inset-0 z-[1] pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-[#E8761A]/5 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-1/4 right-1/4 w-56 sm:w-80 h-56 sm:h-80 bg-[#E8761A]/10 rounded-full blur-3xl animate-float" style={{ animationDelay: "1.5s" }} />
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          {/* Logo */}
          <div
            className={`mb-6 sm:mb-8 transition-all duration-1000 ${
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            <img
              src="https://i.ibb.co.com/kgSskVkk/OB-LOGO.png"
              alt="Oerang Borneo Film Logo"
              data-testid="hero-logo"
              className="h-20 sm:h-28 md:h-36 lg:h-40 w-auto mx-auto drop-shadow-2xl"
            />
          </div>

          {/* Main Title */}
          <h1
            data-testid="hero-title"
            className={`text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-extrabold text-[#E8761A] cinematic-text mb-4 sm:mb-6 text-shadow transition-all duration-1000 delay-300 ${
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            OERANG BORNEO
          </h1>

          {/* Tagline */}
          <p
            data-testid="hero-tagline"
            className={`text-sm sm:text-lg md:text-xl lg:text-2xl text-white/90 font-light tracking-[0.15em] sm:tracking-[0.3em] uppercase mb-8 sm:mb-12 transition-all duration-1000 delay-500 ${
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            Visual Storyteller from Borneo
          </p>

          {/* CTA Buttons */}
          <div
            className={`flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center transition-all duration-1000 delay-700 ${
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            <button
              onClick={() => navigate("/referensi")}
              data-testid="hero-cta-portfolio"
              className="btn-primary flex items-center gap-2 group w-full sm:w-auto justify-center"
            >
              <Play className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
              Lihat Karya Kami
            </button>
            <a
              href="https://wa.me/6289516500862"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-contact"
              className="btn-outline w-full sm:w-auto text-center"
            >
              Hubungi Kami
            </a>
          </div>
        </div>

        {/* Scroll Indicator */}
        <button
          onClick={() => {
            const statsSection = document.getElementById("stats-section");
            statsSection?.scrollIntoView({ behavior: "smooth" });
          }}
          data-testid="hero-scroll-indicator"
          className={`absolute bottom-6 sm:bottom-10 left-1/2 text-white/60 hover:text-[#E8761A] transition-opacity duration-1000 delay-1000 cursor-pointer ${
            isLoaded ? "opacity-100" : "opacity-0"
          }`}
          style={{ transform: 'translateX(-50%)' }}
        >
          <div className="flex flex-col items-center justify-center">
            <span className="text-xs tracking-widest uppercase mb-2">Scroll</span>
            <div className="w-6 h-10 border-2 border-current rounded-full flex justify-center pt-2">
              <div className="w-1 h-2 bg-current rounded-full animate-scroll-bounce" />
            </div>
          </div>
        </button>

        {/* Bottom Gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0A1628] to-transparent z-[5]" />
      </section>

      {/* Stats Section */}
      <section
        id="stats-section"
        data-testid="stats-section"
        className="relative py-16 sm:py-24 lg:py-32 bg-[#0A1628]"
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] sm:w-[800px] h-[300px] sm:h-[400px] bg-[#E8761A]/5 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Header */}
          <div className="text-center mb-10 sm:mb-16 lg:mb-20 animate-fade-in">
            <h2
              data-testid="home-title"
              className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white mb-4 sm:mb-6"
            >
              <span className="text-[#E8761A]">Oerang Borneo</span> Film
            </h2>
            <div className="section-divider mx-auto mb-6 sm:mb-8" />
            <p
              data-testid="home-description"
              className="text-sm sm:text-base lg:text-lg text-white/70 max-w-3xl mx-auto leading-relaxed px-2"
            >
              Oerang Borneo adalah perusahaan perfilman dan industri kreatif yang berdiri
              sejak 2017. Pada tahun 2025, kami resmi memperoleh legalitas perusahaan.
              Kami bergerak di bidang perfilman dan visual storytelling.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
            {[
              { value: "2017", label: "Tahun Berdiri" },
              { value: "8+", label: "Tahun Pengalaman" },
              { value: "10+", label: "Layanan Kreatif" },
              { value: "100%", label: "Dedikasi" },
            ].map((stat, index) => (
              <div
                key={stat.label}
                data-testid={`stat-${index}`}
                className="text-center p-4 sm:p-6 lg:p-8 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 hover:border-[#E8761A]/30 transition-all duration-300"
              >
                <p className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#E8761A] mb-2 sm:mb-3">
                  {stat.value}
                </p>
                <p className="text-white/60 text-xs sm:text-sm lg:text-base">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
